import React from 'react';
import { Box, TextField, Typography, Button } from '@mui/material';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';

export interface ButtonPosition {
  top?: string;
  right?: string;
  bottom?: string;
  left?: string;
}

interface ButtonPositionEditorProps {
  value: ButtonPosition;
  onChange: (position: ButtonPosition) => void;
}

const Container = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.spacing(2)};
`;

const PreviewContainer = styled(Box)`
  position: relative;
  width: 200px;
  height: 200px;
  border: 2px dashed ${p => p.theme.palette.grey[400]};
  border-radius: ${p => p.theme.spacing(1)};
  background-color: ${p => p.theme.palette.grey[50]};
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  gap: 2px;
  padding: 2px;
`;

const PreviewButton = styled(Box)<{ buttonPosition: ButtonPosition }>`
  position: absolute;
  width: 16px;
  height: 16px;
  background-color: ${p => p.theme.palette.primary.main};
  border-radius: 50%;
  z-index: 10;
  ${props => {
    const { top, right, bottom, left } = props.buttonPosition;
    const styles: any = {};

    if (top) styles.top = top;
    if (right) styles.right = right;
    if (bottom) styles.bottom = bottom;
    if (left) styles.left = left;

    return styles;
  }}
`;

const CornerButton = styled(Button)<{ selected: boolean }>`
  background-color: ${p =>
    p.selected ? p.theme.palette.primary.light : p.theme.palette.grey[100]};
  border: 1px solid
    ${p =>
      p.selected ? p.theme.palette.primary.main : p.theme.palette.grey[300]};
  min-width: unset;
  min-height: unset;
  padding: ${p => p.theme.spacing(1)};
  &:hover {
    background-color: ${p =>
      p.selected ? p.theme.palette.primary.main : p.theme.palette.grey[200]};
  }
`;

const InputsContainer = styled(Box)`
  display: flex;
  gap: ${p => p.theme.spacing(1)};
  align-items: center;
`;

export function ButtonPositionEditor({
  value,
  onChange
}: ButtonPositionEditorProps) {
  const { t } = useTranslation();

  // Determine which corner is selected based on the position values
  const getSelectedCorner = ():
    | 'top-left'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-right' => {
    if (value.top !== undefined && value.left !== undefined) return 'top-left';
    if (value.top !== undefined && value.right !== undefined)
      return 'top-right';
    if (value.bottom !== undefined && value.left !== undefined)
      return 'bottom-left';
    return 'bottom-right'; // default
  };

  const selectedCorner = getSelectedCorner();

  const handleCornerChange = (
    corner: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
  ) => {
    const currentVerticalOffset = normalizeValue(getCurrentVerticalOffset());
    const currentHorizontalOffset = normalizeValue(
      getCurrentHorizontalOffset()
    );

    switch (corner) {
      case 'top-left':
        onChange({ top: currentVerticalOffset, left: currentHorizontalOffset });
        break;
      case 'top-right':
        onChange({
          top: currentVerticalOffset,
          right: currentHorizontalOffset
        });
        break;
      case 'bottom-left':
        onChange({
          bottom: currentVerticalOffset,
          left: currentHorizontalOffset
        });
        break;
      case 'bottom-right':
        onChange({
          bottom: currentVerticalOffset,
          right: currentHorizontalOffset
        });
        break;
    }
  };

  const getCurrentVerticalOffset = (): string => {
    const currentValue = value.top || value.bottom || '12';
    return currentValue.replace('px', '');
  };

  const getCurrentHorizontalOffset = (): string => {
    const currentValue = value.left || value.right || '12';
    return currentValue.replace('px', '');
  };

  const normalizeValue = (input: string): string => {
    const numericValue = input.replace(/[^0-9.]/g, '');
    return numericValue ? `${numericValue}px` : '12px';
  };

  const handleVerticalOffsetChange = (newOffset: string) => {
    const normalizedOffset = normalizeValue(newOffset);
    const newPosition: ButtonPosition = {};

    switch (selectedCorner) {
      case 'top-left':
        newPosition.top = normalizedOffset;
        newPosition.left = normalizeValue(getCurrentHorizontalOffset());
        break;
      case 'top-right':
        newPosition.top = normalizedOffset;
        newPosition.right = normalizeValue(getCurrentHorizontalOffset());
        break;
      case 'bottom-left':
        newPosition.bottom = normalizedOffset;
        newPosition.left = normalizeValue(getCurrentHorizontalOffset());
        break;
      case 'bottom-right':
        newPosition.bottom = normalizedOffset;
        newPosition.right = normalizeValue(getCurrentHorizontalOffset());
        break;
    }

    onChange(newPosition);
  };

  const handleHorizontalOffsetChange = (newOffset: string) => {
    const normalizedOffset = normalizeValue(newOffset);
    const newPosition: ButtonPosition = {};

    switch (selectedCorner) {
      case 'top-left':
        newPosition.top = normalizeValue(getCurrentVerticalOffset());
        newPosition.left = normalizedOffset;
        break;
      case 'top-right':
        newPosition.top = normalizeValue(getCurrentVerticalOffset());
        newPosition.right = normalizedOffset;
        break;
      case 'bottom-left':
        newPosition.bottom = normalizeValue(getCurrentVerticalOffset());
        newPosition.left = normalizedOffset;
        break;
      case 'bottom-right':
        newPosition.bottom = normalizeValue(getCurrentVerticalOffset());
        newPosition.right = normalizedOffset;
        break;
    }

    onChange(newPosition);
  };

  return (
    <Container>
      <PreviewContainer>
        <PreviewButton buttonPosition={value} />
        <CornerButton
          size="small"
          selected={selectedCorner === 'top-left'}
          onClick={() => handleCornerChange('top-left')}
        >
          ↖
        </CornerButton>
        <CornerButton
          size="small"
          selected={selectedCorner === 'top-right'}
          onClick={() => handleCornerChange('top-right')}
        >
          ↗
        </CornerButton>
        <CornerButton
          size="small"
          selected={selectedCorner === 'bottom-left'}
          onClick={() => handleCornerChange('bottom-left')}
        >
          ↙
        </CornerButton>
        <CornerButton
          size="small"
          selected={selectedCorner === 'bottom-right'}
          onClick={() => handleCornerChange('bottom-right')}
        >
          ↘
        </CornerButton>
        <Typography
          variant="caption"
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: 'text.secondary',
            pointerEvents: 'none',
            zIndex: 5
          }}
        >
          {t('container')}
        </Typography>
      </PreviewContainer>

      <InputsContainer>
        <Typography variant="body2">{t('vertical')}:</Typography>
        <TextField
          size="small"
          value={getCurrentVerticalOffset()}
          onChange={e => handleVerticalOffsetChange(e.target.value)}
          placeholder="12"
          sx={{ width: '60px' }}
        />
        <Typography variant="caption" color="textSecondary">
          px
        </Typography>

        <Typography variant="body2" sx={{ ml: 2 }}>
          {t('horizontal')}:
        </Typography>
        <TextField
          size="small"
          value={getCurrentHorizontalOffset()}
          onChange={e => handleHorizontalOffsetChange(e.target.value)}
          placeholder="12"
          sx={{ width: '60px' }}
        />
        <Typography variant="caption" color="textSecondary">
          px
        </Typography>
      </InputsContainer>
    </Container>
  );
}
